import { useState, useEffect } from "react";
import _ from "lodash";

import {
  Button,
  Container,
  Card,
  CardContent,
  Typography,
  CardMedia,
  CardActions,
  Grid,
  Box,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CustomLayout from "views/CustomLayout";
import Carousel from "components/Carousel";
import Loader from "components/Loader";
import axiosInstance from "configs/axiosConfig";
import {
  APIRouteConstants,
  authRouteConstants,
} from "constants/routeConstants";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { GLOBAL_CURRENCY } from "constants/appConstants";
import ProductList from "./ProductList";
import ProductCard from "./ProductCard";
import { isMobile } from "react-device-detect";
import { updateUserDetail } from "utils/auth";
import { getAccessToken } from "utils";
import currency from "currency.js";
import { useStore } from "utils/OfflineContext";
import { Carousel as BannerCourousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import BannerCarousel from "./BannerCarousel";
import PrimaryButton from "components/Button";
import { useAuth } from "oidc-react";
import { useTheme, useMediaQuery } from "@mui/material";
let formatCurrency = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: GLOBAL_CURRENCY.code,
  currencyDisplay: "narrowSymbol",
  minimumFractionDigits: 2,
});
function formatCurrencyNew(value, currencySymbol = "$", precision = 2) {
  return currency(value, {
    symbol: `${currencySymbol} `,
    precision: precision,
    separator: ",",
    decimal: ".",
  }).format();
}

const DetailCard = ({ product }) => {
  const webDetails = useSelector((state) => state.webDetails);
  const { websiteInfo } = webDetails;
  const navigate = useNavigate();

  const variant = product?.variants && product?.variants[0];

  return (
    <Box className="slider-card-desktop">
      <Card
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: "14px",
          border: "1px solid rgba(0,0,0,0.08)",
          boxShadow: "0px 6px 18px rgba(0,0,0,0.08)",
          overflow: "hidden",
          cursor: "pointer",
          transition: "all 0.25s ease",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0px 14px 28px rgba(0,0,0,0.14)",
          },
        }}
        onClick={() => navigate(`/product/${variant?.id}`)}
      >
        {/* IMAGE FIXED HEIGHT */}
        <Box
          sx={{
            width: "100%",
            height: 180,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f5f5f5",
          }}
        >
          <img
            src={variant?.images?.[0]?.image || variant?.images?.[0]?.image_url}
            alt={product?.name}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "fill", // ✅ full image, no stretch
            }}
          />
        </Box>

        {/* CONTENT FIXED SPACE */}
        <CardContent
          sx={{
            px: 2,
            pt: 1.5,
            pb: 1,
            flexGrow: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.70rem",
              fontWeight: 400,
              color: "var(--text-muted)",

              lineHeight: 1.3,
              minHeight: "32px", // ✅ fixed space for 2 lines
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {_.truncate(_.trim(product?.name), { length: 25 })}
          </Typography>

          <Typography
            sx={{
              fontSize: "0.80rem",
              fontWeight: 550,
              color: "#111",
              mt: 1,
            }}
          >
            {formatCurrencyNew(
              variant?.price || 0,
              websiteInfo?.currency_symbol,
            )}
          </Typography>
        </CardContent>

        {/* BUTTON ALWAYS SAME PLACE */}
        <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
          <PrimaryButton
            fullWidth
            size="small"
            variant="outlined"
            sx={{
              backgroundColor: "#fff",
              color: "#111",
              border: "1px solid rgba(0,0,0,0.25)",
              borderRadius: "6px",
              fontSize: "0.7rem",
              fontWeight: 500,
              textTransform: "none",
              padding: "6px 10px",
              "&:hover": {
                backgroundColor: "#f4f4f4",
                border: "1px solid rgba(0,0,0,0.4)",
              },
            }}
            onClick={() => navigate(`/product/${variant?.id}`)}
          >
            View
          </PrimaryButton>
        </CardActions>
      </Card>
    </Box>
  );
};

const DraftSection = ({
  title,
  description,
  collection,
  explore,
  style = {},
}) => {
  var navigate = useNavigate();
  const theme = useTheme();

  // ✅ Mobile + Small
  const isMobileOrSmall = useMediaQuery(theme.breakpoints.down("sm"));
  return collection.length > 0 ? (
    <Container maxWidth="lg" className="draft-section center" sx={style}>
      <Box sx={{ maxWidth: "1250px", width: "100%", margin: "0 auto" }}>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center", // content center
            justifyContent: "center",

            mb: 3,
            mt: 3,
            px: { xs: 2, sm: 2, md: 10 },
          }}
        >
          <Typography variant="h6" className="title">
            {title}
          </Typography>

          {description && (
            <Typography
              className="description"
              sx={{
                mt: 1,
                color: "var(--text-muted)",
                textAlign: "left",
                fontSize: "var(--fs-sm)",
              }}
            >
              {description}
            </Typography>
          )}

          {explore && (
            <Box sx={{ mt: 2 }}>
              <PrimaryButton
                sx={{
                  border: "1px solid rgba(0,0,0,0.25)",
                  borderRadius: "6px",
                }}
                minWidth={180}
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate("/products")}
              >
                Explore
              </PrimaryButton>
            </Box>
          )}
        </Box>
        <div className="content-section">
          {isMobileOrSmall ? (
            //  Mobile + Small → ProductCard
            <Grid container padding={"10px"} spacing={1}>
              {collection.map((product) => (
                <Grid item xs={6} sm={6} key={product.id} marginTop={1}>
                  <ProductCard product={product} />
                </Grid>
              ))}
            </Grid>
          ) : (
            // Tablet + Desktop → DetailCard
            <Carousel>
              {collection.map((product) => (
                <DetailCard key={product.id} product={product} />
              ))}
            </Carousel>
          )}
        </div>
      </Box>
    </Container>
  ) : (
    ""
  );
};

const bgImage = require("../../assets/images/Webbie_default_banner.jpg");

const ResponsiveBgBox = ({ websiteInfo, ...props }) => (
  <>
    <Box
      sx={{
        display: { xs: "none", sm: "none", md: "block" },
      }}
      className="banner-content"
    >
      <CardMedia
        component={"img"}
        src={
          _.get(websiteInfo, "banner", "")
            ? `${_.get(websiteInfo, "banner")}`
            : `${bgImage}`
        }
        sx={{
          width: "100%",
          minHeight: "30rem",
          maxHeight: "30rem",
          position: "absolute",
          objectFit: "fill",
        }}
      />
      {props.children}
    </Box>
    <Box
      sx={{
        display: { xs: "block", sm: "block", md: "none" },
      }}
      className="banner-overlay"
    >
      <img className="banner-image" src={_.get(websiteInfo, "banner")} />
      <div className="banner-detail">{props.children}</div>
    </Box>
  </>
);

const Landing = () => {
  const [loader, setLoader] = useState(false);
  const webDetails = useSelector((state) => state.webDetails);
  const { websiteInfo } = webDetails;
  const { isStoreOn, toggleStore, setStoreOn, setStoreOff } = useStore();
  const [topCollection, setTopCollection] = useState([]);
  const [featureCollection, setFeatureCollection] = useState([]);
  const navigate = useNavigate();
  const auth = useAuth();

  const normalizeProduct = (item) => {
    return {
      ...item,
      id: item?.id || item?.id,
      name: item?.product_name || item?.name,
      product_image:
        (item?.images && item?.images[0]?.image) || item?.product_image,
    };
  };

  function updateMetaDescription(newDescription) {
    const metaDescriptionTag = document.querySelector(
      "meta[name='description']",
    );
    if (metaDescriptionTag) {
      metaDescriptionTag.setAttribute("content", newDescription);
    } else {
      // If it doesn't exist, create it
      const newMetaTag = document.createElement("meta");
      newMetaTag.setAttribute("name", "description");
      newMetaTag.setAttribute("content", newDescription);
      document.head.appendChild(newMetaTag); // Append it to the head
    }
  }

  useEffect(() => {
    if (auth?.userData)
      localStorage.setItem("token-expiry", auth?.userData?.expires_at);
  }, []);

  // Function to update the favicon
  function updateFavicon(newFavicon) {
    const faviconLink = document.getElementById("favicon");
    if (faviconLink) {
      faviconLink.setAttribute("href", newFavicon);
    } else {
      const newFaviconLink = document.createElement("link");
      newFaviconLink.setAttribute("rel", "icon");
      newFaviconLink.setAttribute("href", newFavicon);
      document.head.appendChild(newFaviconLink);
    }
  }

  // Function to update the apple-touch-icon
  function updateAppleTouchIcon(newAppleTouchIcon) {
    const appleTouchIconLink = document.getElementById("apple-touch-icon");
    if (appleTouchIconLink) {
      appleTouchIconLink.setAttribute("href", newAppleTouchIcon);
    } else {
      const newAppleTouchIconLink = document.createElement("link");
      newAppleTouchIconLink.setAttribute("rel", "apple-touch-icon");
      newAppleTouchIconLink.setAttribute("href", newAppleTouchIcon);
      document.head.appendChild(newAppleTouchIconLink);
    }
  }

  // Function to update the manifest
  function updateManifest(newManifest) {
    const manifestLink = document.getElementById("manifest");
    if (manifestLink) {
      manifestLink.setAttribute("href", newManifest);
    } else {
      const newManifestLink = document.createElement("link");
      newManifestLink.setAttribute("rel", "manifest");
      newManifestLink.setAttribute("href", newManifest);
      document.head.appendChild(newManifestLink);
    }
  }

  const getUserDetails = async (category) => {
    let token = getAccessToken();
    if (!token) return;
    setLoader(true);
    let res = await axiosInstance.get(APIRouteConstants.AUTH.ME);
    let { data } = res;

    console.log("user", data);
    if (data && !_.isEmpty(data?.data)) {
      localStorage.setItem("u_user_data", JSON.stringify(data?.data));
      updateUserDetail(data.data);
    }
    setLoader(false);
  };

  useEffect(() => {
    localStorage.removeItem("vouchercode");
    const userData = localStorage.getItem("u_user_data");
    if (webDetails && webDetails?.websiteInfo?.store_name === "The Vet Store") {
      document.title = "The Vet Store";
      updateMetaDescription("The Vet Store");
      updateFavicon("/favicon1.ico"); // Fallback to default favicon
      updateAppleTouchIcon("/vetstore-512x512.png"); // Fallback to default apple-touch-icon
      updateManifest("/manifestvet.json"); // Fallback to default manifest
    }
    console.log("webDetails", websiteInfo?.whatsapp_status);
    setStoreOff(websiteInfo?.whatsapp_status);
    if (webDetails && webDetails?.websiteInfo?.store_name) {
      document.title = `Digital ordering solution | ${webDetails?.websiteInfo?.store_name}`;
      updateMetaDescription(
        `Digital ordering solution | ${webDetails?.websiteInfo?.store_name}`,
      );
      updateFavicon("/favicon.ico"); // Fallback to default favicon
      updateAppleTouchIcon(
        webDetails?.websiteInfo?.banner
          ? webDetails?.websiteInfo?.banner
          : "/logo192.png",
      ); // Fallback to default apple-touch-icon
      updateManifest("/manifest.json"); // Fallback to default manifest
    }

    if (
      window.location.hostname.includes("alora") &&
      window.dataLayer &&
      userData
    ) {
      window.dataLayer.push({
        event: "email_tracking",
        userEmail: JSON.parse(userData)?.email,
      });
    }
  }, [webDetails]);

  const getTopCollection = async () => {
    setLoader(true);
    let res = await axiosInstance.get(
      APIRouteConstants.DASHBOARD.TOP_COLLECTION,
    );
    let { data, response } = res;

    if (data) {
      const filteredDisabledCollection = data?.filter(
        (item) => item?.variants[0]?.is_disabled === false,
      );
      console.log(
        "topcollection",
        data?.filter((item) => item?.variants[0]?.is_disabled === false),
      );
      const normalized = Array.isArray(filteredDisabledCollection)
        ? filteredDisabledCollection.map(normalizeProduct)
        : [];

      setTopCollection(normalized);
    }
    setLoader(false);
  };

  const getFeatureCollection = async () => {
    setLoader(true);
    let res = await axiosInstance.get(
      APIRouteConstants.DASHBOARD.FEATURE_COLLECTION,
    );
    let { data, response } = res;

    if (data) {
      const filteredDisabledCollection = data?.filter(
        (item) => item?.variants[0]?.is_disabled === false,
      );
      console.log(
        "topcollection",
        data?.filter((item) => item?.variants[0]?.is_disabled === false),
      );
      const normalized = Array.isArray(filteredDisabledCollection)
        ? filteredDisabledCollection.map(normalizeProduct)
        : [];
      setFeatureCollection(normalized);
    }
    setLoader(false);
  };

  useEffect(() => {
    getTopCollection();
    getFeatureCollection();
    getUserDetails();
  }, []);

  return (
    <CustomLayout>
      <Loader open={loader} />
      <BannerCarousel
        websiteInfo={_.get(websiteInfo, "banner", [])}
        bgImage={bgImage}
      />

      <DraftSection
        explore={true}
        title="Top Collection"
        collection={topCollection}
        description="These top favorites have been flying off the shelves! Grab them now. Don't miss out on the trendiest and most sought-after products - shop today!"
      />
      <DraftSection
        explore={true}
        title="Featured"
        collection={featureCollection}
        style=" margin-bottom: 3rem ;"
        description="Discover our curated collection of must-have products. Shop now and redefine your world with us."
      />
    </CustomLayout>
  );
};

export default Landing;
